import React, {useState} from 'react';
import classNames from 'classnames';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {Button, Icon, Popup} from '@deviceinsight/ng-ui-basic-components';
import {GridColorPicker} from '@deviceinsight/ng-ui-components';
import {Asset} from '@deviceinsight/ng-ui-api-client';

import SelectKpiButton from '../SelectKpiButton';
import SelectAssetButton from '../SelectAssetButton';
import {KpiWidgetItem} from '../../types';
import {ChartSeriesType} from '../../kpiUtils';
import ChartTypeIcon from './ChartTypeIcon';
import {StyleApi, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';

export type Props = {
	context: WidgetProps['context'];
	item: KpiWidgetItem;
	asset: Asset | null | undefined;
	itemIndex: number;
	withUnit: boolean;
	withChartType: boolean;
	setKpiSelection: (index: number, assetUrn: string) => void;
	setAssetSelection: (index: number) => void;
	onItemChange: (arg0: number, arg1: KpiWidgetItem) => void;
	onItemRemove: (arg0: number) => void;
};

const KpiItem = ({
	item,
	asset,
	itemIndex,
	withUnit,
	withChartType,
	onItemRemove,
	onItemChange,
	setKpiSelection,
	setAssetSelection,
	context
}: Props) => {
	const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isOver} = useSortable({
		id: item.uuid
	});
	const assetGroupContext = context.type === 'assetGroup';
	const kpiClassName = classNames({
		item_kpi: true,
		group_context: assetGroupContext
	});
	const kpiButtonDisabled = assetGroupContext && !item.assetUrn;
	const [selectingChartType, setSelectingChartType] = useState(false);
	return (
		<tr
			key={`itemRow-${itemIndex}`}
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition: isOver ? transition : undefined
			}}
			{...attributes}>
			<td className="customDragHandle">
				<span className="handle" data-testid="drag-handle" ref={setActivatorNodeRef} {...listeners}>
					<span className="symbol">⋮⋮</span>
				</span>
			</td>
			<td className="item-label">
				<input
					type="text"
					value={item.label}
					onChange={({target: {value}}: React.ChangeEvent<HTMLInputElement>) =>
						onItemChange(itemIndex, {...item, label: value})
					}
				/>
			</td>
			{assetGroupContext && (
				<td className="item-asset">
					<SelectAssetButton assetName={asset?.name} selectAsset={() => setAssetSelection(itemIndex)} />
				</td>
			)}
			<td className={kpiClassName}>
				<SelectKpiButton
					selectKpi={() => {
						const urn = asset?.urn || item.assetUrn;
						if (urn) setKpiSelection(itemIndex, urn);
					}}
					kpiName={item.kpiName}
					disabled={kpiButtonDisabled}
				/>
			</td>
			{withUnit && (
				<td className="item-unit">
					<input
						type="text"
						value={item.unit}
						onChange={({target: {value}}: React.ChangeEvent<HTMLInputElement>) =>
							onItemChange(itemIndex, {...item, unit: value})
						}
						title="Unit"
					/>
				</td>
			)}
			<td className="item-color">
				<GridColorPicker
					inline={false}
					colors={StyleApi.getColorPalette()}
					value={item.color}
					onPick={(color) => onItemChange(itemIndex, {...item, color})}
					popperAlignment="left"
				/>
			</td>
			{withChartType && (
				<td className="item-charttype">
					<Popup
						handler={
							<ChartTypeIcon
								chartType={item.chartType || 'column'}
								onSelect={() => setSelectingChartType(!selectingChartType)}
							/>
						}
						active={selectingChartType}
						onHide={() => setSelectingChartType(false)}>
						<div className="chart_type_selector">
							{Object.keys(ChartSeriesType).map((chartTypeKey: string) => {
								const chartType = (ChartSeriesType as Record<string, string>)[chartTypeKey];
								return (
									<ChartTypeIcon
										chartType={chartType}
										key={chartTypeKey}
										onSelect={(chartType) => {
											onItemChange(itemIndex, {...item, chartType});
										}}
									/>
								);
							})}
						</div>
					</Popup>
				</td>
			)}
			<td className="item-remove">
				<Button onClick={() => onItemRemove(itemIndex)}>
					<Icon del />
				</Button>
			</td>
		</tr>
	);
};

export default KpiItem;
